import { v4 as uuidv4 } from 'uuid';
import { query } from '../config/database.js';
import { debugLogger } from '../utils/logger.js';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Client-side implementation that uses API calls
const clientSideImplementation = {
    /**
     * Queue a domain for processing via API call
     * @param {string} domain - Domain to process
     * @param {string} userId - ID of user who owns the domain
     * @param {string} organizationId - ID of organization (if available)
     * @param {boolean} forceQueue - Whether to force queue the domain
     * @returns {Promise<Object>} Created job and domain_info entry
     */
    queueDomainForProcessing: async (domain, userId, organizationId = null, forceQueue = false) => {
        try {
            console.log('Client-side queueDomainForProcessing called', { domain, userId, organizationId, forceQueue });
            
            // Make API call to queue domain
            const response = await fetch('/api/domains/queue', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-User-ID': userId
                },
                body: JSON.stringify({
                    domain,
                    organizationId,
                    forceQueue
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to queue domain');
            }
            
            const data = await response.json();
            return {
                domainId: data.domainId,
                jobId: data.jobId,
                alreadyQueued: data.alreadyQueued
            };
        } catch (error) {
            console.error('Error in client-side queueDomainForProcessing:', error);
            throw error;
        }
    }
};

// Server-side implementation (original code)
const serverSideImplementation = {
    /**
     * Creates a new background job
     * @param {string} jobType - Type of job to create
     * @param {Object} payload - Job payload data
     * @param {string} priority - Job priority (high, standard, low)
     * @returns {Promise<Object>} Created job object
     */
    createJob: async (jobType, payload, priority = 'standard') => {
        try {
            if (!jobType) {
                throw new Error('Job type is required');
            }
            
            if (!payload) {
                throw new Error('Job payload is required');
            }
            
            const jobId = uuidv4();
            const [result] = await query(
                'INSERT INTO background_jobs (id, job_type, priority, status, payload) VALUES (?, ?, ?, ?, ?)',
                [jobId, jobType, priority, 'pending', JSON.stringify(payload)]
            );

            // Check if the insert was successful
            if (!result || result.affectedRows !== 1) {
                throw new Error('Failed to insert job into background_jobs table');
            }

            debugLogger.info('BACKGROUND_JOB', 'Created new job', { 
                jobId, 
                jobType,
                priority,
                affectedRows: result.affectedRows
            });
            
            return { id: jobId, type: jobType, priority, status: 'pending', payload };
        } catch (error) {
            debugLogger.error('BACKGROUND_JOB', 'Failed to create job', {
                jobType,
                priority,
                error: error.message,
                stack: error.stack
            });
            throw error;
        }
    },

    /**
     * Updates a job's status and related fields
     * @param {string} jobId - ID of the job to update
     * @param {string} status - New status
     * @param {Object} [result] - Job result data
     * @param {string} [errorMessage] - Error message if job failed
     */
    updateJobStatus: async (jobId, status, result = null, errorMessage = null) => {
        try {
            if (!jobId) {
                throw new Error('Job ID is required');
            }
            
            if (!status) {
                throw new Error('Status is required');
            }
            
            debugLogger.info('BACKGROUND_JOB', 'Updating job status', { 
                jobId, 
                status,
                hasResult: !!result,
                hasError: !!errorMessage
            });
            
            const updates = ['status = ?'];
            const params = [status];

            if (status === 'processing') {
                updates.push('started_at = NOW()');
            } else if (status === 'completed' || status === 'failed') {
                updates.push('completed_at = NOW()');
            }

            if (result) {
                updates.push('result = ?');
                params.push(JSON.stringify(result));
            }

            if (errorMessage) {
                updates.push('error_message = ?');
                params.push(errorMessage);
            }

            params.push(jobId);

            const [updateResult] = await query(
                `UPDATE background_jobs SET ${updates.join(', ')} WHERE id = ?`,
                params
            );
            
            // Check if the update was successful
            if (!updateResult || updateResult.affectedRows !== 1) {
                throw new Error(`Failed to update job status. Job ID ${jobId} may not exist.`);
            }

            debugLogger.info('BACKGROUND_JOB', 'Updated job status', { 
                jobId, 
                status,
                affectedRows: updateResult.affectedRows
            });
        } catch (error) {
            debugLogger.error('BACKGROUND_JOB', 'Failed to update job status', {
                jobId,
                status,
                error: error.message,
                stack: error.stack
            });
            throw error;
        }
    },

    /**
     * Gets the next pending job of a specific type
     * @param {string} jobType - Type of job to get
     * @returns {Promise<Object|null>} Next pending job or null if none found
     */
    getNextPendingJob: async (jobType) => {
        try {
            if (!jobType) {
                throw new Error('Job type is required');
            }
            
            debugLogger.info('BACKGROUND_JOB', 'Getting next pending job', { jobType });
            
            const [jobs] = await query(
                'SELECT * FROM background_jobs WHERE job_type = ? AND status = ? ORDER BY priority ASC, created_at ASC LIMIT 1',
                [jobType, 'pending']
            );

            const hasJob = jobs && jobs.length > 0;
            
            if (hasJob) {
                debugLogger.info('BACKGROUND_JOB', 'Found pending job', { 
                    jobId: jobs[0].id,
                    jobType
                });
            } else {
                debugLogger.info('BACKGROUND_JOB', 'No pending jobs found', { jobType });
            }

            return hasJob ? jobs[0] : null;
        } catch (error) {
            debugLogger.error('BACKGROUND_JOB', 'Failed to get next pending job', {
                jobType,
                error: error.message,
                stack: error.stack
            });
            throw error;
        }
    },

    /**
     * Gets a job by its ID
     * @param {string} jobId - ID of the job to get
     * @returns {Promise<Object|null>} Job object or null if not found
     */
    getJob: async (jobId) => {
        try {
            if (!jobId) {
                throw new Error('Job ID is required');
            }
            
            debugLogger.info('BACKGROUND_JOB', 'Getting job by ID', { jobId });
            
            const [jobs] = await query(
                'SELECT * FROM background_jobs WHERE id = ?',
                [jobId]
            );

            const hasJob = jobs && jobs.length > 0;
            
            if (hasJob) {
                debugLogger.info('BACKGROUND_JOB', 'Found job', { 
                    jobId,
                    jobType: jobs[0].job_type,
                    status: jobs[0].status
                });
            } else {
                debugLogger.info('BACKGROUND_JOB', 'Job not found', { jobId });
            }

            return hasJob ? jobs[0] : null;
        } catch (error) {
            debugLogger.error('BACKGROUND_JOB', 'Failed to get job', {
                jobId,
                error: error.message,
                stack: error.stack
            });
            throw error;
        }
    },

    /**
     * Queues a domain for processing (brand info gathering)
     * @param {string} domain - Domain to process
     * @param {string} userId - ID of user who owns the domain
     * @param {string} organizationId - ID of organization (if available)
     * @param {boolean} forceQueue - Whether to force queue the domain
     * @returns {Promise<Object>} Created job and domain_info entry
     */
    queueDomainForProcessing: async (domain, userId, organizationId = null, forceQueue = false) => {
        try {
            if (!domain) {
                throw new Error('Domain is required');
            }
            
            if (!userId) {
                throw new Error('User ID is required');
            }
            
            debugLogger.info('DOMAIN_QUEUE', 'Queueing domain for processing', { 
                domain, 
                userId, 
                organizationId,
                forceQueue
            });
            
            // First, check if domain already exists in domain_info table
            const [existingDomains] = await query(
                'SELECT * FROM domain_info WHERE domain = ?',
                [domain]
            );

            let domainId;
            let alreadyQueued = false;

            if (existingDomains && existingDomains.length > 0) {
                // Domain already exists, use its ID
                domainId = existingDomains[0].id;
                
                // Check if it was recently processed and we're not forcing a requeue
                if (!forceQueue && existingDomains[0].last_processed_at) {
                    const lastProcessed = new Date(existingDomains[0].last_processed_at);
                    const fiveMinutesAgo = new Date();
                    fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);
                    
                    if (lastProcessed > fiveMinutesAgo) {
                        alreadyQueued = true;
                        debugLogger.info('DOMAIN_QUEUE', 'Domain was processed recently, but requeuing due to force flag', { 
                            domain, 
                            domainId,
                            lastProcessed
                        });
                    }
                }
                
                // Update the domain status if we're forcing a requeue
                if (forceQueue) {
                    await query(
                        'UPDATE domain_info SET status = ? WHERE id = ?',
                        ['pending', domainId]
                    );
                }
                
                debugLogger.info('DOMAIN_QUEUE', 'Domain already exists in domain_info', { 
                    domain, 
                    domainId,
                    alreadyQueued
                });
            } else {
                // Create new domain_info entry
                const [result] = await query(
                    `INSERT INTO domain_info 
                    (domain, status, added_by_user_id, created_at) 
                    VALUES (?, ?, ?, NOW())`,
                    [domain, 'pending', userId]
                );
                
                // Check if the insert was successful
                if (!result || !result.insertId) {
                    throw new Error('Failed to insert domain into domain_info table');
                }
                
                domainId = result.insertId;
                debugLogger.info('DOMAIN_QUEUE', 'Added new domain to domain_info', { 
                    domain, 
                    domainId,
                    affectedRows: result.affectedRows
                });
            }

            // Create background job for domain processing
            const job = await serverSideImplementation.createJob('domain_processing', { 
                domainId,
                domain,
                userId,
                organizationId,
                source: forceQueue ? 'manual' : 'registration'
            }, forceQueue ? 'high' : 'standard');  // Higher priority for forced queues

            debugLogger.info('DOMAIN_QUEUE', 'Created job for domain processing', {
                domain,
                domainId,
                jobId: job.id,
                priority: forceQueue ? 'high' : 'standard'
            });

            return { 
                domainId, 
                jobId: job.id,
                alreadyQueued
            };
        } catch (error) {
            debugLogger.error('DOMAIN_QUEUE', 'Failed to queue domain for processing', {
                domain,
                userId,
                organizationId,
                forceQueue,
                error: error.message,
                stack: error.stack
            });
            throw error;
        }
    }
};

// Choose the appropriate implementation based on environment
const implementation = isBrowser ? clientSideImplementation : serverSideImplementation;

// Export the queueDomainForProcessing function for both client and server
export const queueDomainForProcessing = implementation.queueDomainForProcessing;

// Export server-side functions directly
export const createJob = !isBrowser ? serverSideImplementation.createJob : undefined;
export const updateJobStatus = !isBrowser ? serverSideImplementation.updateJobStatus : undefined;
export const getNextPendingJob = !isBrowser ? serverSideImplementation.getNextPendingJob : undefined;
export const getJob = !isBrowser ? serverSideImplementation.getJob : undefined; 